import AnalyticService from "@/src/api/AnalyticService.js";
import { units } from "@/src/constants/analyticConstants.js";

export function toSnakeCase(str) {
  if (str === null || str === undefined) return "";
  const s = String(str).trim();
  let result = s.replace(/\s+/g, "_");
  result = result.replace(/([a-z0-9])([A-Z])/g, "$1_$2");
  result = result.replace(/[^\w_]+/g, "_");
  result = result.replace(/__+/g, "_");
  result = result.replace(/^_+|_+$/g, "");

  return result.toLowerCase();
}

export function convertKeysToSnakeCase(data) {
  if (Array.isArray(data)) {
    return data.map((item) => convertKeysToSnakeCase(item));
  }

  if (typeof data === "object" && data !== null) {
    return Object.keys(data).reduce((acc, key) => {
      const snakeCaseKey = toSnakeCase(key);
      acc[snakeCaseKey] = data[key];
      return acc;
    }, {});
  }

  return data;
}

export const toFetchContactCount = async (params) => {
  try {
    const response = await AnalyticService.V2.fetchContactCount(params);
    if (response?.success) {
      return response?.data?.count;
    }
  } catch (error) {
    console.error("An error occurred while fetching contacts count: ", error);
  }
};

export const toFetchCompanyCount = async (params) => {
  try {
    const response = await AnalyticService.V2.fetchCompanyCount(params);
    if (response?.success) {
      return response?.data?.count;
    }
  } catch (error) {
    console.error("An error occurred while fetching companies count: ", error);
  }
};

export const toFetchLeadCount = async (params) => {
  try {
    const response = await AnalyticService.V2.fetchLeadCount(params);
    if (response?.success) {
      return response?.data?.count;
    }
  } catch (error) {
    console.error("An error occurred while fetching leads count: ", error);
  }
};

export const toFetchEmailSentCount = async (params) => {
  try {
    const response = await AnalyticService.V2.fetchEmailSentCount(params);
    if (response?.success) {
      return response?.data?.email_count;
    }
  } catch (error) {
    console.error("An error occurred while fetching email sent count: ", error);
  }
};

export const toFetchEmailCampaignCount = async (params) => {
  try {
    const response = await AnalyticService.V2.fetchEmailCampaignCount(params);
    if (response?.success) {
      return response?.data?.count;
    }
  } catch (error) {
    console.error("An error occurred while fetching email sent count: ", error);
  }
};

export const toFetchCallCount = async (params) => {
  try {
    const response = await AnalyticService.V2.fetchCallCount(params);
    if (response?.success) {
      return response?.data?.count;
    }
  } catch (error) {
    console.error("An error occurred while fetching call done count: ", error);
  }
};

export const toFetchLinkedinCount = async (params) => {
  try {
    const response = await AnalyticService.V2.fetchLinkedinCount(params);
    if (response?.success) {
      return response?.data?.count;
    }
  } catch (error) {
    console.error("An error occurred while fetching linkedin count: ", error);
  }
};

export const formatAnalyticsValue = (
  value,
  unit = null,
  currencySymbol = "$",
) => {
  if (
    value === null ||
    value === undefined ||
    value === "-" ||
    value === "Error"
  ) {
    return "-";
  }

  const numValue = Number(value);
  if (isNaN(numValue)) return "-";

  const formattedNumber = Number.isInteger(numValue)
    ? numValue
    : Number(numValue.toFixed(2));

  const formattedValue = formattedNumber.toLocaleString("en-US");

  if (!unit) {
    return formattedValue;
  }

  if (formattedNumber === 0) {
    return unit.suffix
      ? `0${unit.suffix}`
      : unit.prefix
        ? `${currencySymbol}0`
        : `0 ${unit.plural}`;
  }

  if (unit.prefix === "$") {
    return `${currencySymbol}${formattedValue}`;
  }

  if (unit.suffix === "%") {
    return `${formattedNumber}${unit.suffix}`;
  }

  if (unit === units.SECOND) {
    const hours = Math.floor(formattedNumber / 3600);
    const minutes = Math.floor((formattedNumber % 3600) / 60);
    const seconds = formattedNumber % 60;

    let result = "";

    if (hours > 0) {
      result += `${hours} ${hours === 1 ? units.HOUR.single : units.HOUR.plural}`;
      if (minutes > 0)
        result += ` ${minutes} ${minutes === 1 ? units.MINUTE.single : units.MINUTE.plural}`;
    } else if (minutes > 0) {
      result += `${minutes} ${minutes === 1 ? units.MINUTE.single : units.MINUTE.plural}`;
      if (seconds > 0)
        result += ` ${Math.round(seconds)} ${Math.round(seconds) === 1 ? units.SECOND.single : units.SECOND.plural}`;
    } else {
      result = `${formattedNumber} ${formattedNumber === 1 ? units.SECOND.single : units.SECOND.plural}`;
    }

    return result;
  }

  return `${formattedNumber} ${formattedNumber === 1 ? unit.single : unit.plural}`;
};

export const toFetchChartByID = async (id) => {
  try {
    if (!id) {
      return;
    }

    const response = await AnalyticService.V3.fetchChartByID(id);
    if (response?.success) {
      return response?.data;
    }
  } catch (error) {
    console.error("An error occurred while fetching chart details: ", error);
    return null;
  }
};
